import { Box, Stack, StackProps, useTheme } from '@mui/material';
import { SystemProps } from '@mui/system/Box/Box';
import React, { ReactNode } from 'react';

import { Children, Component, ReturnComponent, SxStyle } from '../../utils/reactTypes';

export type HorizontalStackProps = {
    children: ReactNode;
    spacing?: number | string;
    fullWidth?: boolean;
    flexWrap?: SystemProps['flexWrap'];
    useFlexGap?: boolean;
    alignItems?: SystemProps['alignItems'];
    sx?: SxStyle;
};

export const HorizontalStack = ({
    children,
    spacing = 0,
    fullWidth = true,
    flexWrap,
    useFlexGap,
    alignItems = 'center',
    sx = {},
    ...rest
}: HorizontalStackProps & StackProps): ReturnComponent => (
    <Stack
        direction={'row'}
        alignItems={alignItems}
        spacing={spacing}
        width={fullWidth ? '100%' : undefined}
        sx={sx}
        {...rest}
        flexWrap={flexWrap}
        useFlexGap={useFlexGap}
    >
        {children}
    </Stack>
);

export const HorizontalStackInnerGrow = ({
    children,
    ...props
}: HorizontalStackProps & {
    children: Component[];
}): ReturnComponent => (
    <HorizontalStack alignItems={'start'} {...props}>
        {children
            .filter((child) => child !== null)
            // We don't want to render null children. The other children should fill the space.
            .map((child, idx) => (
                <Box key={idx} flex={1}>
                    {child}
                </Box>
            ))}
    </HorizontalStack>
);

export const VerticalStack = ({
    children,
    spacing = 0,
    fullWidth = true,
    sx = {},
    ...rest
}: {
    children: Children;
    spacing?: number;
    fullWidth?: boolean;
    sx?: SxStyle;
} & StackProps): ReturnComponent => {
    // Without `alignItems={'flex-start'}`, the children end up taking the width of this
    //  parent container, instead of their content width. That leads to unexpected things,
    //  like say if you have chip components inside, they'll get stretched out to full width.
    const alignItems = fullWidth ? undefined : 'flex-start';

    return (
        <Stack direction={'column'} spacing={spacing} alignItems={alignItems} sx={sx} {...rest}>
            {children}
        </Stack>
    );
};

export const DistributeHorizontally = ({
    children,
    spacing = 0,
    style = {},
    ...rest
}: {
    children: Children;
    spacing?: number;
    style?: React.CSSProperties;
}): ReturnComponent => {
    const theme = useTheme();
    return (
        <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            style={{ gap: theme.spacing(spacing), width: '100%', ...style }}
            {...rest}
        >
            {children}
        </Box>
    );
};

export const distributeHorizontally: SystemProps = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

export const FloatRight = ({ children }: { children: Children }): ReturnComponent => (
    <DistributeHorizontally>
        <>&nbsp;</>
        <Box>{children}</Box>
    </DistributeHorizontally>
);

export const VerticalHeight = ({ height }: { height: string }): ReturnComponent => {
    return <Box height={height}>&nbsp;</Box>;
};

export const VerticalSpace = ({ spacing }: { spacing: number }): ReturnComponent => {
    const theme = useTheme();
    return <VerticalHeight height={theme.spacing(spacing)} />;
};
